import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

const DateTimeSection = ({
    initialDate,
    initialStartTime,
    initialEndTime,
    onDateChange,
    onStartTimeChange,
    onEndTimeChange,
    onCrossDateChange,
    isEditMode,
    initialCrossDate
}) => {
    const [date, setDate] = useState(dayjs(initialDate));
    const [startTime, setStartTime] = useState(initialStartTime ? dayjs(initialStartTime, 'HH:mm') : null);
    const [endTime, setEndTime] = useState(initialEndTime ? dayjs(initialEndTime, 'HH:mm') : null);

    useEffect(() => {
        if (initialDate) setDate(dayjs(initialDate));
    }, [initialDate]);

    useEffect(() => {
        if (initialStartTime) {
            setStartTime(dayjs(initialStartTime, 'HH:mm'));
        }
        if (initialEndTime) {
            setEndTime(dayjs(initialEndTime, 'HH:mm'));
        }
    }, [initialStartTime, initialEndTime]);

    const handleDateChange = (newValue) => {
        setDate(newValue);
        onDateChange(newValue.format('YYYY-MM-DD'));
    };

    const handleStartTimeChange = (newValue) => {
        setStartTime(newValue);
        onStartTimeChange(newValue ? newValue.format('HH:mm') : '');
    };

    const handleEndTimeChange = (newValue) => {
        setEndTime(newValue);
        onEndTimeChange(newValue ? newValue.format('HH:mm') : '');
    };

    const handleCrossDateChange = (e) => {
        onCrossDateChange(e.target.checked);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimeSectionWrapper>
                <TimePickerWrapper>
                    <DatePicker
                        value={date}
                        onChange={handleDateChange}
                        views={['year', 'month', 'day']}
                        disabled={!isEditMode}
                        sx={{
                            width: '220px',
                            '& .MuiInputBase-input': {
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '16px',
                                padding: '10px 12px',
                                textAlign: 'left',
                                color: 'black',
                                backgroundColor: 'white',
                                WebkitTextFillColor: '#515351',
                                cursor: isEditMode ? 'pointer' : 'default',
                            },
                            '& .MuiInputAdornment-root': {
                                marginLeft: '4px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderRadius: '8px',
                            },
                            '& .MuiIconButton-root': {
                                display: isEditMode ? 'flex' : 'none',
                                backgroundColor: 'white',
                            },
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                            },
                        }}
                    />
                    <CustomTimePicker
                        value={startTime}
                        onChange={handleStartTimeChange}
                        required
                    />
                    <TimeSpan>-</TimeSpan>
                    <CustomTimePicker
                        value={endTime}
                        onChange={handleEndTimeChange}
                        required
                    />
                    <CrossDateCheckbox>
                        <input
                            type="checkbox"
                            checked={initialCrossDate || false}
                            onChange={handleCrossDateChange}
                            id="crossDateCheckbox"
                        />
                        <label htmlFor="crossDateCheckbox">Next Day</label>
                    </CrossDateCheckbox>
                </TimePickerWrapper>
            </DateTimeSectionWrapper>
        </LocalizationProvider>
    );
};

const DateTimeSectionWrapper = styled.div`
    align-self: start;
    display: flex;
    margin-top: 8px;
    align-items: center;
    gap: 16px;
    font-size: 14px;
    font-weight: 500;
    flex-wrap: nowrap;
    width: 100%;
    min-width: 0;
`;

const TimePickerWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: nowrap;
    width: 100%;
    min-width: 0;

    & .MuiFormControl-root,
    & .MuiTextField-root {
        min-width: 0;
        flex-shrink: 1;
    }

    @media (max-width: 600px) {
        gap: 8px;
        flex-wrap: wrap;
    }
`;

const CrossDateCheckbox = styled.div`
    display: flex;
    font-size: 14px;
    align-items: center;
    input {
        margin-right: 5px;
    }
    font-weight: 500;
    gap: 6px;
    margin-top: 0; /* Keep in line */
    z-index: 1000;
    white-space: nowrap; 
`;

const CustomTimePicker = styled(TimePicker)({
    width: '220px',
    '& .MuiInputBase-input': {
        fontFamily: 'Inter, sans-serif',
        fontSize: '16px',
        padding: '10px 12px',
        textAlign: 'left',
        cursor: 'default',
        backgroundColor: 'white'
    },
    '& .MuiInputAdornment-root': {
        marginLeft: '4px',  // default spacing is often best
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderRadius: '8px',
    },
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'white',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#534af5', // custom border color when selected (focus)
    },
});

const TimeSpan = styled.span`
  font-weight: 600;
  margin: 0 6px;
`;

export default DateTimeSection;